/**
 * Phase 12 — quality records, corrective actions, maintenance, notifications,
 * the management dashboard and data import (decisions D161–D178).
 *
 * NOTHING HERE DESCRIBES A MATERIAL (CLAUDE.md section 4). A closed quality
 * record, a done maintenance entry or a count on a dashboard says nothing about
 * whether any material passed.
 */
// ---------------------------------------------------------------------------
// Quality records (D163, D164)
// ---------------------------------------------------------------------------
export const QUALITY_RECORD_KIND = {
    COMPLAINT: 'complaint',
    OBSERVATION: 'observation',
    NONCONFORMITY: 'nonconformity',
};
export const ALL_QUALITY_RECORD_KINDS = Object.freeze(Object.values(QUALITY_RECORD_KIND));
export const QUALITY_RECORD_STATUS = {
    OPEN: 'open',
    IN_PROGRESS: 'in_progress',
    CLOSED: 'closed',
    CANCELLED: 'cancelled',
};
export const ALL_QUALITY_RECORD_STATUSES = Object.freeze(Object.values(QUALITY_RECORD_STATUS));
/** Closed and cancelled are final (D163). */
export function isQualityRecordFinal(status) {
    return status === QUALITY_RECORD_STATUS.CLOSED || status === QUALITY_RECORD_STATUS.CANCELLED;
}
export const CORRECTIVE_ACTION_STATUS = {
    OPEN: 'open',
    COMPLETED: 'completed',
    VERIFIED: 'verified',
    CANCELLED: 'cancelled',
};
export const ALL_CORRECTIVE_ACTION_STATUSES = Object.freeze(Object.values(CORRECTIVE_ACTION_STATUS));
/** An action that still blocks its record's closure (D163). */
export function isCorrectiveActionOutstanding(status) {
    return status === CORRECTIVE_ACTION_STATUS.OPEN || status === CORRECTIVE_ACTION_STATUS.COMPLETED;
}
// ---------------------------------------------------------------------------
// Maintenance and faults (D167, D168)
// ---------------------------------------------------------------------------
export const MAINTENANCE_KIND = {
    PLANNED: 'planned',
    FAULT: 'fault',
};
export const ALL_MAINTENANCE_KINDS = Object.freeze(Object.values(MAINTENANCE_KIND));
export const MAINTENANCE_STATUS = {
    PLANNED: 'planned',
    DONE: 'done',
    REPORTED: 'reported',
    RESOLVED: 'resolved',
    CANCELLED: 'cancelled',
};
export const ALL_MAINTENANCE_STATUSES = Object.freeze(Object.values(MAINTENANCE_STATUS));
// ---------------------------------------------------------------------------
// Notifications (D169–D172)
// ---------------------------------------------------------------------------
export const NOTIFICATION_KIND = {
    ASSIGNMENT_DELAYED: 'assignment_delayed',
    RESULT_RETURNED: 'result_returned',
    CALIBRATION_DUE: 'calibration_due',
    MAINTENANCE_DUE: 'maintenance_due',
    REPORT_ISSUED: 'report_issued',
    CORRECTIVE_ACTION_ASSIGNED: 'corrective_action_assigned',
    FAULT_REPORTED: 'fault_reported',
};
export const ALL_NOTIFICATION_KINDS = Object.freeze(Object.values(NOTIFICATION_KIND));
/** The work item a notification leads to (D169). */
export const NOTIFICATION_ENTITY_TYPE = {
    TEST_ASSIGNMENT: 'test_assignment',
    TEST_ATTEMPT: 'test_attempt',
    EQUIPMENT: 'equipment',
    MAINTENANCE_RECORD: 'maintenance_record',
    REPORT: 'report',
    CORRECTIVE_ACTION: 'corrective_action',
    QUALITY_RECORD: 'quality_record',
};
export const ALL_NOTIFICATION_ENTITY_TYPES = Object.freeze(Object.values(NOTIFICATION_ENTITY_TYPE));
/** What happened to a notification's email copy (D162). */
export const NOTIFICATION_EMAIL_STATUS = {
    NOT_REQUESTED: 'not_requested',
    SENT: 'sent',
    FAILED: 'failed',
};
// ---------------------------------------------------------------------------
// Data import (D175)
// ---------------------------------------------------------------------------
export const IMPORT_KIND = {
    CLIENTS: 'clients',
    PROJECTS: 'projects',
    CATALOGUE: 'catalogue',
};
export const ALL_IMPORT_KINDS = Object.freeze(Object.values(IMPORT_KIND));
/**
 * The header each import kind expects, in order. Unknown columns are refused.
 *
 * Clients and projects have ONE `name` since the UX pass (D183). A file still
 * written with the old `name_ar` / `name_en` columns is accepted
 * (`IMPORT_LEGACY_NAME_COLUMNS`) and merged by the same rule migration 0012
 * applied (D184); a file naming BOTH forms is refused rather than one being
 * silently dropped. Catalogue entries keep both names (D187).
 */
export const IMPORT_COLUMNS = Object.freeze({
    [IMPORT_KIND.CLIENTS]: [
        'name',
        'client_type',
        'vat_registration_number',
        'commercial_registration_number',
        'contact_email',
        'contact_phone',
        'notes',
    ],
    [IMPORT_KIND.PROJECTS]: ['client_number', 'name', 'reference', 'notes'],
    // Code and names only. No price and no field of test science (CLAUDE.md 4).
    [IMPORT_KIND.CATALOGUE]: ['code', 'name_ar', 'name_en', 'reference_standard', 'standard_version', 'notes'],
});
/** The old two-column name, still accepted for clients and projects (D184). */
export const IMPORT_LEGACY_NAME_COLUMNS = Object.freeze({
    [IMPORT_KIND.CLIENTS]: ['name_ar', 'name_en'],
    [IMPORT_KIND.PROJECTS]: ['name_ar', 'name_en'],
});
export const IMPORT_LIMITS = Object.freeze({
    MAX_ROWS: 500,
    MAX_BYTES: 512 * 1024,
});
//# sourceMappingURL=operations.js.map